import {
  BoxType,
  PAGE_FILL_BOX_ID,
  type CustomTemplate,
  type CustomTemplateBox,
  type CustomVectorTemplateBox,
  type RawColor,
} from './custom-template';

/**
 * Графический фон: BACKGROUND-бокс с картинкой (в группе после PSD-разбора тоже). Заливка страницы под ним
 * невидима — «Цвет фона» такому развороту не даётся. Пустой BACKGROUND без источника фон не графический.
 */
export function hasRasterBackground(boxes: readonly CustomTemplateBox[]): boolean {
  return boxes.some(box => {
    if (box.type === BoxType.GROUP) return hasRasterBackground(box.boxes);
    if (box.type !== BoxType.BACKGROUND) return false;
    const { fileId, url, pixelData } = box.params;
    return fileId !== undefined || url !== undefined || pixelData !== undefined;
  });
}

// Id живёт в dto-fotoclass-templates: по нему извлечение фона узнаёт заливку страницы и не роняет её.
export { PAGE_FILL_BOX_ID };
// A page with no fill box prints white.
export const DEFAULT_PAGE_FILL: string = rawColorToHex({ r: 255, g: 255, b: 255, a: 255 });

// Strict `#rrggbb[aa]` only — the album page fill (album-document/collect-render-boxes.ts) drops anything else.
const HEX = /^#[0-9a-f]{6}([0-9a-f]{2})?$/i;

export function pageFillOf(model: CustomTemplate): string | null {
  const box = model.boxes[0];
  if (!box || box.id !== PAGE_FILL_BOX_ID || box.type !== BoxType.VECTOR) return null;
  const fill = box.params.fill;
  return fill?.kind === 'solid' ? rawColorToHex(fill.color) : null;
}

export function withPageFill(model: CustomTemplate, hex: string): CustomTemplate {
  if (!HEX.test(hex)) throw new Error(`page fill expects #rrggbb[aa], got ${hex}`);
  return { ...model, boxes: [fillBox(model, hex), ...model.boxes.filter(box => box.id !== PAGE_FILL_BOX_ID)] };
}

function fillBox(model: CustomTemplate, hex: string): CustomVectorTemplateBox {
  const { originalWidth: w, originalHeight: h } = model;
  return {
    type: BoxType.VECTOR,
    id: PAGE_FILL_BOX_ID,
    rect: [0, 0, w, h],
    rotation: 0,
    // Covers the whole page: unlocked it would swallow every click on empty canvas.
    locked: true,
    params: {
      origination: { kind: 'rectangle', bbox: { x: 0, y: 0, w, h } },
      paths: [],
      fill: { kind: 'solid', color: rawColorFromHex(hex) },
    },
  };
}

export function rawColorFromHex(hex: string): RawColor {
  const at = (i: number): number => parseInt(hex.slice(i, i + 2), 16);
  return { r: at(1), g: at(3), b: at(5), a: hex.length === 9 ? at(7) : 255 };
}

/** `#rrggbb`, `aa` appended only when not opaque — the form the page fill and its readers use. */
export function rawColorToHex(color: RawColor): string {
  const two = (value: number): string => Math.round(value).toString(16).padStart(2, '0');
  return `#${two(color.r)}${two(color.g)}${two(color.b)}${color.a === 255 ? '' : two(color.a)}`;
}
